import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import LandingNavbar from "../components/landing/LandingNavbar";
import Footer from "../components/landing/Footer";
import FAQSection from "../components/seo/FAQSection";
import JsonLd from "../components/seo/JsonLd";
import { Button, Input } from "../components/ui";
import NotFound from "./NotFound";
import usePageSeo from "../hooks/usePageSeo";
import { isIndiaSite } from "../data/seo/siteEntity";
import { pushToDataLayer } from "../services/analytics";
import {
    APPROVED_PRICE,
    CTA_BODY,
    CTA_HEADING,
    CTA_PRICE_NOTE,
    EMPTY_INPUTS,
    EMPTY_STATE,
    ESTIMATE_NOTE,
    FAQS,
    FIELDS,
    FORMULAS,
    H1,
    HOW_TO_LOWER,
    INDUSTRIES,
    INTRO,
    METHODOLOGY,
    MISSED_CALL_CALCULATOR_URL,
    RESULTS_SIGNUP_PATH,
    SEO_DESCRIPTION,
    SEO_TITLE,
    SIGNUP_PATH,
    WHO_USES_LINE,
    ZERO_MISSED,
    calculateMissedCalls,
    formatCount,
    formatDollars,
    formatRate,
    formatSignedDollars,
    validateInputs,
    type CalculatorInputs,
    type FieldKey,
} from "../data/tools/missedCallCalculator";

const TOOL_EVENT = {
    product: "flogpt",
    tool_name: "missed-call-calculator",
} as const;

function trackTool(event: "tool_start" | "tool_complete"): void {
    pushToDataLayer({ event, ...TOOL_EVENT });
}

function trackSignupClick(ctaLocation: "results" | "cta_block"): void {
    pushToDataLayer({ event: "signup_cta_click", ...TOOL_EVENT, cta_location: ctaLocation });
}

const webApplicationSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Missed Call Calculator",
    url: MISSED_CALL_CALCULATOR_URL,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Any",
    offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
    },
    publisher: {
        "@type": "Organization",
        name: "FloGPT",
        legalName: "Pulse Real LLC",
        url: "https://flogpt.com",
    },
};

const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://flogpt.com/" },
        {
            "@type": "ListItem",
            position: 2,
            name: "Missed-Call Calculator",
            item: MISSED_CALL_CALCULATOR_URL,
        },
    ],
};

function FieldHint({ fieldKey }: { fieldKey: FieldKey }) {
    if (fieldKey === "calls") {
        return (
            <>
                Calls from potential customers in a typical month. Your phone system's call log is the best source. Leave
                out spam and calls from existing customers if you can.
            </>
        );
    }
    if (fieldKey === "missPct") {
        return <>Share of those calls nobody answers: after hours, busy lines, or calls sent to voicemail.</>;
    }
    if (fieldKey === "noCallbackPct") {
        return <>Your best estimate. Check which missed numbers never show up again in your call log.</>;
    }
    if (fieldKey === "convPct") {
        return <>Of the callers you do speak to, the share who end up booking or buying.</>;
    }
    if (fieldKey === "avgValue") {
        return <>What a typical new job, booking or sale is worth to you. Use your own sales records.</>;
    }
    if (fieldKey === "aiPct") {
        return (
            <>
                Your own estimate of how many of today's missed calls an AI voice agent would pick up. Leave blank to
                skip the recoverable-revenue line.
            </>
        );
    }
    if (fieldKey === "planCost") {
        return (
            <>
                The plan you'd use. floGPT: {APPROVED_PRICE}. Voice agents need a paid plan.{" "}
                <Link to="/pricing" className="font-medium text-orange-600 hover:text-orange-500">
                    See pricing
                </Link>
            </>
        );
    }
    return (
        <>
            Calls and SMS are billed as usage on top of the plan. Estimate yours from the usage rates on the{" "}
            <Link to="/pricing" className="font-medium text-orange-600 hover:text-orange-500">
                pricing page
            </Link>
            .
        </>
    );
}

function MissedCallCalculatorView() {
    usePageSeo({
        title: SEO_TITLE,
        description: SEO_DESCRIPTION,
        canonical: MISSED_CALL_CALCULATOR_URL,
        ogImage: "/images/og-default.jpg",
        // flogpt.in is on hold. This page must not advertise an en-IN alternate.
        hreflang: false,
    });

    const [inputs, setInputs] = useState<CalculatorInputs>(EMPTY_INPUTS);
    const [touched, setTouched] = useState<Partial<Record<FieldKey, boolean>>>({});
    const [attempted, setAttempted] = useState(false);
    const started = useRef(false);
    const completed = useRef(false);

    const errors = useMemo(() => validateInputs(inputs), [inputs]);
    const result = useMemo(() => calculateMissedCalls(inputs), [inputs]);

    useEffect(() => {
        if (!result || completed.current) return;
        completed.current = true;
        trackTool("tool_complete");
    }, [result]);

    const setField = (key: FieldKey, value: string) => {
        if (!started.current && value.trim() !== "") {
            started.current = true;
            trackTool("tool_start");
        }
        setInputs((current) => ({ ...current, [key]: value }));
    };

    const clearAll = () => {
        setInputs(EMPTY_INPUTS);
        setTouched({});
        setAttempted(false);
    };

    return (
        <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="missed-call-calculator">
            <LandingNavbar />
            <main className="mx-auto max-w-3xl px-6 pb-8 pt-24 lg:px-8 sm:pt-28">
                <nav aria-label="Breadcrumb" className="text-sm text-slate-400">
                    <ol className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <li>
                            <Link to="/" className="text-slate-300 transition-colors hover:text-orange-400">
                                Home
                            </Link>
                        </li>
                        <li className="flex items-center gap-2">
                            <ChevronRightIcon className="h-3 w-3 shrink-0 opacity-60" aria-hidden="true" />
                            <span aria-current="page" className="font-medium text-white">
                                Missed-Call Calculator
                            </span>
                        </li>
                    </ol>
                </nav>

                <h1 className="mt-8 text-3xl font-bold tracking-tight text-white sm:text-4xl">{H1}</h1>
                <p className="mt-6 text-lg leading-relaxed text-slate-200">{INTRO}</p>

                <div className="mt-10 rounded-2xl border border-slate-800 bg-white p-6 text-slate-900 shadow-lg sm:p-8">
                    <div className="space-y-5">
                        {FIELDS.map((field) => {
                            const message = errors[field.key];
                            const showError = !!message && (attempted || touched[field.key]);
                            const hintId = `mcc-${field.key}-hint`;
                            const errorId = `mcc-${field.key}-error`;
                            return (
                                <Input
                                    key={field.key}
                                    id={`mcc-${field.key}`}
                                    label={field.label}
                                    placeholder={field.placeholder}
                                    value={inputs[field.key]}
                                    onChange={(event) => setField(field.key, event.target.value)}
                                    onBlur={() => setTouched((current) => ({ ...current, [field.key]: true }))}
                                    error={showError ? message : undefined}
                                    errorId={errorId}
                                    hint={<FieldHint fieldKey={field.key} />}
                                    hintId={hintId}
                                    persistHint
                                    inputMode={field.kind === "whole" ? "numeric" : "decimal"}
                                    autoComplete="off"
                                    spellCheck={false}
                                    aria-required={field.required}
                                    aria-invalid={showError ? "true" : "false"}
                                    aria-describedby={showError ? `${hintId} ${errorId}` : hintId}
                                />
                            );
                        })}
                    </div>
                    <div className="mt-6 flex flex-wrap items-center gap-4">
                        <Button
                            type="button"
                            onClick={() => {
                                setAttempted(true);
                                document.getElementById("calculator-results")?.focus();
                            }}
                        >
                            Calculate
                        </Button>
                        <button
                            type="button"
                            onClick={clearAll}
                            className="text-sm font-medium text-orange-600 hover:text-orange-500"
                        >
                            Clear all fields
                        </button>
                    </div>
                    <noscript>
                        <p className="mt-4 text-sm text-slate-600">{EMPTY_STATE}</p>
                    </noscript>
                </div>

                <section
                    id="calculator-results"
                    tabIndex={-1}
                    className="mt-8 rounded-2xl border border-slate-800 bg-slate-900/70 p-6 sm:p-8"
                >
                    {result ? (
                        <div className="space-y-4 text-slate-200 leading-relaxed">
                            <p>
                                <strong className="text-white">About {formatCount(result.missedCalls)} missed calls a month.</strong>{" "}
                                You get {formatCount(result.calls)} calls a month and miss {formatRate(result.missPct)}% of them.
                            </p>
                            <p>
                                <strong className="text-white">About {formatCount(result.lostLeads)} lost leads a month.</strong>{" "}
                                {formatRate(result.noCallbackPct)}% of missed callers don't call back.
                            </p>
                            <p>
                                <strong className="text-white">
                                    Estimated lost revenue: ${formatDollars(result.lostRevMo)} a month (${formatDollars(result.lostRevYr)} a year).
                                </strong>{" "}
                                That's at your close rate of {formatRate(result.convPct)}% and average customer value of ${formatDollars(result.avgValue)}.
                            </p>
                            {result.recoverableMo !== null && result.recoverableYr !== null && result.aiPct !== null ? (
                                <p>
                                    <strong className="text-white">
                                        Estimated recoverable revenue: ${formatDollars(result.recoverableMo)} a month (${formatDollars(result.recoverableYr)} a year)
                                    </strong>{" "}
                                    if an AI voice agent answered {formatRate(result.aiPct)}% of the calls you miss today.
                                </p>
                            ) : null}
                            {result.netMo !== null && result.planCost !== null && result.usageCost !== null ? (
                                <p>
                                    <strong className="text-white">
                                        After the ${formatDollars(result.planCost)} plan cost and ${formatDollars(result.usageCost)} usage you entered: {formatSignedDollars(result.netMo)} a month.
                                    </strong>
                                </p>
                            ) : null}
                            {result.costsIncomplete ? (
                                <p>
                                    Add a plan cost and your expected call usage to see a figure after costs. Usage rates are
                                    on the{" "}
                                    <Link to="/pricing" className="font-medium text-orange-400 hover:text-orange-300">
                                        pricing page
                                    </Link>
                                    .
                                </p>
                            ) : null}
                            <p>{ESTIMATE_NOTE}</p>
                            {result.missedCalls === 0 ? (
                                <p>{ZERO_MISSED}</p>
                            ) : (
                                <p>
                                    <strong className="text-white">
                                        Answer the {formatCount(result.missedCalls)} calls you're missing with an AI voice agent
                                    </strong>{" "}
                                    <Link
                                        to={RESULTS_SIGNUP_PATH}
                                        onClick={() => trackSignupClick("results")}
                                        className="font-medium text-orange-400 hover:text-orange-300"
                                    >
                                        Start free
                                    </Link>
                                </p>
                            )}
                        </div>
                    ) : (
                        <p className="text-slate-300">{EMPTY_STATE}</p>
                    )}
                </section>

                <h2 className="mt-12 text-2xl font-bold tracking-tight text-white sm:text-3xl">How the math works</h2>
                <ul className="mt-4 list-disc space-y-2 pl-5 text-slate-300 leading-relaxed">
                    {FORMULAS.map((line) => (
                        <li key={line}>{line}</li>
                    ))}
                </ul>

                <h2 className="mt-12 text-2xl font-bold tracking-tight text-white sm:text-3xl">How to lower missed calls</h2>
                <ul className="mt-4 list-disc space-y-3 pl-5 text-slate-300 leading-relaxed">
                    {HOW_TO_LOWER.map((item) => (
                        <li key={item.title}>
                            <strong className="text-white">{item.title}</strong> {item.body}
                        </li>
                    ))}
                    <li>
                        <strong className="text-white">Cover after-hours and busy periods.</strong> An{" "}
                        <Link to="/ai-voice-agents" className="font-medium text-orange-400 hover:text-orange-300">
                            AI voice agent
                        </Link>{" "}
                        can answer inbound calls, answer common questions and capture the caller's details for your team. See{" "}
                        <Link
                            to="/blog/how-to-set-up-ai-voice-agent-inbound-calls"
                            className="font-medium text-orange-400 hover:text-orange-300"
                        >
                            how to set up an AI voice agent for inbound calls
                        </Link>
                        .
                    </li>
                </ul>

                <h2 className="mt-12 text-2xl font-bold tracking-tight text-white sm:text-3xl">Who uses this</h2>
                <p className="mt-4 text-slate-300 leading-relaxed">
                    {INDUSTRIES.map((industry, index) => (
                        <span key={industry.href}>
                            {index > 0 ? " · " : null}
                            <Link to={industry.href} className="font-medium text-orange-400 hover:text-orange-300">
                                {industry.label}
                            </Link>
                        </span>
                    ))}
                </p>
                <p className="mt-3 text-slate-300 leading-relaxed">{WHO_USES_LINE}</p>

                <section className="mt-12 rounded-2xl border border-slate-800 bg-slate-900/70 p-6 sm:p-8">
                    <h2 className="text-2xl font-bold tracking-tight text-white">{CTA_HEADING}</h2>
                    <p className="mt-3 text-slate-300 leading-relaxed">{CTA_BODY}</p>
                    <p className="mt-3 font-medium text-white">{CTA_PRICE_NOTE}</p>
                    <p className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2">
                        <Link
                            to={SIGNUP_PATH}
                            onClick={() => trackSignupClick("cta_block")}
                            className="inline-flex items-center rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 px-5 py-2.5 text-sm font-semibold text-white"
                        >
                            Start free
                        </Link>
                        <span aria-hidden="true" className="text-slate-500">
                            ·
                        </span>
                        <Link to="/contact" className="text-sm font-semibold text-orange-400 hover:text-orange-300">
                            Talk to a human
                        </Link>
                    </p>
                </section>
            </main>

            <FAQSection id="missed-call-calculator" heading="FAQ" items={FAQS.map((item) => ({ ...item }))} />

            <p className="mx-auto max-w-3xl px-6 pb-16 text-sm leading-relaxed text-slate-400 lg:px-8">
                <strong className="text-slate-200">How this calculator works.</strong> {METHODOLOGY.replace(/^How this calculator works\. /, "")}
            </p>
            <Footer />
            <JsonLd id="missed-call-calculator-app" data={webApplicationSchema} />
            <JsonLd id="missed-call-calculator-breadcrumb" data={breadcrumbSchema} />
        </div>
    );
}

/**
 * flogpt.com only. flogpt.in is on hold: the same route renders the not-found
 * page there, with no hreflang and no calculator canonical.
 */
export default function MissedCallCalculatorPage() {
    if (isIndiaSite()) return <NotFound />;
    return <MissedCallCalculatorView />;
}
